import { useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  createCheckoutSession,
  createPortalSession,
  getPlans,
  tierName,
  type PublicPlan,
} from "@/api/billing";
import { familiesQuery, subscriptionQuery } from "@/lib/queries";
import { extractApiError, httpStatus } from "@/api/client";
import { formatDate } from "@/lib/format";
import { Card, ErrorText, InkButton, LoadingBlock, QuietButton, SectionLabel } from "@/components/ui";
import { Modal } from "@/components/Modal";

/**
 * /plans — the web billing surface (webBillingBuild P-WB.4, locked
 * D-WB.1–8). Retires the "See plans → /account" stubs. Price parity
 * with the app (D-WB.3), differentiated by the web-only Library.
 * Buying runs Stripe hosted Checkout (server-minted session URL —
 * no Stripe.js on this page); managing runs the Customer Portal
 * (D-WB.2). A family billed through a store rail gets the D-WB.1
 * rail-switch card: the web plan starts when the store coverage
 * lapses (zero double-charge), and only the user can cancel the
 * store subscription on their device.
 */

const PlansPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  return (
    <AppShell user={user}>
      <PlansView user={user} />
    </AppShell>
  );
};

type Interval = "monthly" | "annual";

const PlansView = ({ user }: { user: UserResponse }) => {
  const search = useSearch({ from: "/plans" }) as { checkout?: string };
  const plans = useQuery({ queryKey: ["plans"], queryFn: getPlans, staleTime: 300_000 });
  const subscription = useQuery(subscriptionQuery());
  const families = useQuery(familiesQuery);
  const [interval, setInterval] = useState<Interval>("monthly");
  const [railBlock, setRailBlock] = useState<{
    rail: string; paidThrough: string; priceId: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const home = families.data?.find((f) => f.family_id === user.home_family_id);
  const isOwner =
    home?.members.find((m) => m.user_id === user.user_id)?.role === "owner";
  const activeRail = subscription.data?.active_rail ?? null;
  const currentTier = subscription.data?.tier;

  const checkout = useMutation({
    mutationFn: (payload: { price_id: string; switch?: boolean }) =>
      createCheckoutSession(payload),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err, payload) => {
      const apiErr = extractApiError(err);
      if (httpStatus(err) === 409 && apiErr?.code === "other_rail_blocked") {
        setRailBlock({
          rail: apiErr.fields?.rail || "the app store",
          paidThrough: apiErr.fields?.paid_through || "",
          priceId: payload.price_id,
        });
        return;
      }
      setError(apiErr?.message ?? "Couldn't start checkout. Please try again.");
    },
  });

  const portal = useMutation({
    mutationFn: createPortalSession,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't open plan management. Please try again.",
      ),
  });

  if (plans.isPending) return <LoadingBlock label="Loading plans…" />;
  if (plans.isError || !plans.data) {
    return (
      <div className="mx-auto max-w-xl py-10 text-center">
        <p className="font-serif text-[15px] italic text-ink-soft">
          We couldn't load the plans. Please try again.
        </p>
        <div className="mt-5">
          <QuietButton onClick={() => plans.refetch()}>Try again</QuietButton>
        </div>
      </div>
    );
  }

  const webPlans = plans.data.filter(
    (p) => p.stripe_price_monthly || p.stripe_price_annual,
  );

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-serif text-[28px] font-medium text-ink">Plans</h1>
      <p className="mt-1 font-serif text-[15px] text-ink-soft">
        Pay on the web and the Library comes with it — bring in your
        whole photo history, organize it, and share from it.
      </p>

      {search.checkout === "success" && (
        <Card className="mt-5 border border-sage/60 p-5">
          <p className="font-sans text-[14px] leading-[22px] text-ink">
            Payment received — your plan updates here in a moment.
            Welcome to web billing; the Library is yours.
          </p>
        </Card>
      )}
      {search.checkout === "cancelled" && (
        <Card className="mt-5 p-5">
          <p className="font-sans text-[14px] text-ink-soft">
            Checkout was cancelled — nothing was charged.
          </p>
        </Card>
      )}

      {activeRail === "stripe" ? (
        <Card className="mt-6 p-6">
          <SectionLabel>Your plan</SectionLabel>
          <p className="font-sans text-[16px] font-medium text-ink">
            {currentTier ? tierName(currentTier) : "Your plan"} — billed on
            the web
          </p>
          <p className="mt-1 font-sans text-[13px] text-ink-soft">
            Change plan, update your card, or cancel any time.
          </p>
          <div className="mt-4">
            <InkButton
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
            >
              {portal.isPending ? "Opening…" : "Manage plan"}
            </InkButton>
          </div>
        </Card>
      ) : (
        <>
          <div className="mt-6 flex gap-2.5">
            {(
              [["monthly", "Monthly"], ["annual", "Annual"]] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={interval === key}
                onClick={() => setInterval(key)}
                className={`tap-target rounded-full px-5 py-2 font-sans text-[13px] font-medium transition-colors ${
                  interval === key
                    ? "bg-ink text-white"
                    : "border border-sand text-ink hover:border-taupe"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {webPlans.map((plan) => (
              <PlanCard
                key={plan.tier}
                plan={plan}
                interval={interval}
                isCurrent={currentTier === plan.tier}
                canBuy={isOwner}
                busy={checkout.isPending}
                onBuy={(priceId) => {
                  setError(null);
                  checkout.mutate({ price_id: priceId });
                }}
              />
            ))}
          </div>
          {!isOwner && (
            <p className="mt-3 font-sans text-[13px] text-ink-soft">
              Only the circle owner can change the plan.
            </p>
          )}
        </>
      )}

      <ErrorText>{error}</ErrorText>

      {railBlock && (
        <Modal
          title="Your plan is billed through the app store"
          onClose={() => setRailBlock(null)}
        >
          <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
            This family's plan is paid through{" "}
            {railBlock.rail === "apple" ? "Apple" : railBlock.rail}
            {railBlock.paidThrough &&
              ` and is covered until ${formatDate(railBlock.paidThrough)}`}
            . Move your billing to the web and your web plan starts the
            moment that coverage ends — you won't be charged twice.
          </p>
          <p className="mt-3 font-sans text-[14px] leading-[22px] text-ink-soft">
            One step only you can do:{" "}
            <span className="font-medium text-ink">
              cancel the {railBlock.rail === "apple" ? "Apple" : "store"}{" "}
              subscription in your device's Settings
            </span>{" "}
            so it doesn't renew.
          </p>
          <div className="mt-6 flex justify-end gap-3">
            <QuietButton onClick={() => setRailBlock(null)}>
              Not now
            </QuietButton>
            <InkButton
              onClick={() => {
                checkout.mutate({
                  price_id: railBlock.priceId,
                  switch: true,
                });
                setRailBlock(null);
              }}
              disabled={checkout.isPending}
            >
              Switch my billing
            </InkButton>
          </div>
        </Modal>
      )}
    </div>
  );
};

const PlanCard = ({
  plan,
  interval,
  isCurrent,
  canBuy,
  busy,
  onBuy,
}: {
  plan: PublicPlan;
  interval: Interval;
  isCurrent: boolean;
  canBuy: boolean;
  busy: boolean;
  onBuy: (priceId: string) => void;
}) => {
  const priceId =
    interval === "monthly" ? plan.stripe_price_monthly : plan.stripe_price_annual;
  const priceHint =
    interval === "monthly"
      ? plan.display_price_hint_monthly
      : plan.display_price_hint_annual;
  return (
    <Card
      className={`p-6 ${plan.most_popular ? "border-2 border-blush" : "border border-sand/40"}`}
    >
      {plan.most_popular && (
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-blush">
          Most popular
        </p>
      )}
      <p className="font-serif text-[20px] font-medium text-ink">{plan.name}</p>
      <p className="mt-0.5 font-serif text-[13px] italic text-ink-soft">
        {plan.tagline}
      </p>
      <p className="mt-3 font-sans text-[18px] font-medium text-ink">
        {priceHint}
      </p>
      <p className="mt-2 font-sans text-[13px] text-ink">
        ✓ Includes the Library on the web
      </p>
      <div className="mt-5">
        <InkButton
          onClick={() => priceId && onBuy(priceId)}
          disabled={!priceId || !canBuy || busy || isCurrent}
        >
          {isCurrent ? "Current plan" : busy ? "Starting…" : `Choose ${plan.name}`}
        </InkButton>
      </div>
    </Card>
  );
};

export const Route = createFileRoute("/plans")({
  loader: ({ context }) => requireAuth(context.queryClient),
  validateSearch: (search) => ({
    checkout: typeof search.checkout === "string" ? search.checkout : undefined,
  }),
  component: PlansPage,
});
